import type { NleAsset, NleType } from '../dto/tenantModel';
import { invariant } from './invariant';

/**
 * Extracts the file extension from a filename (including the dot).
 * @param filename - The filename to extract from
 * @returns The extension (e.g., '.prproj', '.drp') or empty string if none
 * @throws Error if filename is not a valid string
 */
export function getFileExtension(filename: string): string {
  invariant(typeof filename === 'string', 'getFileExtension: filename must be a string');

  const trimmed = filename.trim();
  if (trimmed.length === 0) {
    return '';
  }

  const lastDot = trimmed.lastIndexOf('.');
  return lastDot > 0 ? trimmed.substring(lastDot) : '';
}

/**
 * Extracts the display name from a filename by removing the extension.
 * @param filename - The filename to extract from
 * @returns The filename without extension
 * @throws Error if filename is not a valid string
 */
export function getDisplayNameWithoutExtension(filename: string): string {
  invariant(typeof filename === 'string', 'getDisplayNameWithoutExtension: filename must be a string');

  const trimmed = filename.trim();
  if (trimmed.length === 0) {
    return trimmed;
  }

  const lastDot = trimmed.lastIndexOf('.');
  return lastDot > 0 ? trimmed.substring(0, lastDot) : trimmed;
}

/**
 * Gets the appropriate file extension for a given NLE type.
 * @param nleType - The NLE type (premiere, dvr, or undefined)
 * @returns The file extension (e.g., '.prproj', '.drp')
 */
export function getExtensionForNleType(nleType?: NleType): string {
  if (nleType === 'dvr') return '.drp';
  if (nleType === 'premiere') return '.prproj';
  return '.prproj';
}

export const KNOWN_NLE_EXTENSIONS = ['.prproj', '.drp', '.epr', '.xml'] as const;

/**
 * Checks if a filename has a known NLE file extension.
 * @param filename - The filename to check
 * @returns True if the filename ends with a known NLE extension
 */
export function hasKnownNleExtension(filename: string): boolean {
  return KNOWN_NLE_EXTENSIONS.some(ext => filename.toLowerCase().endsWith(ext));
}

/**
 * Normalizes NleAsset to ensure both name (display) and fileName (actual file) exist.
 * Handles backward compatibility with old data that only has 'name'.
 *
 * @param asset - The asset to normalize
 * @returns Normalized asset with both name and fileName
 * @throws Error if asset is invalid or missing required fields
 */
export function normalizeNleAsset(asset: NleAsset): NleAsset {
  invariant(asset && typeof asset === 'object', 'normalizeNleAsset: asset must be a valid object');
  invariant(asset.itemId && typeof asset.itemId === 'string', 'normalizeNleAsset: asset.itemId is required');

  if (asset.fileName && asset.name) {
    invariant(typeof asset.fileName === 'string', 'normalizeNleAsset: asset.fileName must be a string');
    invariant(typeof asset.name === 'string', 'normalizeNleAsset: asset.name must be a string');
    return asset;
  }

  if (asset.fileName && !asset.name) {
    invariant(typeof asset.fileName === 'string', 'normalizeNleAsset: asset.fileName must be a string');
    return {
      ...asset,
      name: getDisplayNameWithoutExtension(asset.fileName)
    };
  }

  if (!asset.fileName && asset.name) {
    invariant(typeof asset.name === 'string', 'normalizeNleAsset: asset.name must be a string');

    if (hasKnownNleExtension(asset.name)) {
      return {
        ...asset,
        fileName: asset.name,
        name: getDisplayNameWithoutExtension(asset.name)
      };
    } else {
      const ext = getExtensionForNleType(asset.nleType);
      return {
        ...asset,
        fileName: `${asset.name}${ext}`
      };
    }
  }

  invariant(false, 'normalizeNleAsset: asset must have either name or fileName');
}

/**
 * Creates an NleAsset from upload data, properly separating display name and filename.
 *
 * @param uploadedFileName - The original filename from the uploaded file
 * @param displayName - The user-edited display name (without extension)
 * @param itemId - The item ID
 * @param nleType - The NLE type
 * @param visibleTo - Visibility settings (optional)
 * @param language - Language setting for presets (optional)
 * @returns Formatted NleAsset
 * @throws Error if required parameters are invalid
 */
export function createNleAssetFromUpload(
  uploadedFileName: string,
  displayName: string,
  itemId: string,
  nleType?: NleType,
  visibleTo?: string[],
  language?: string | null
): NleAsset {
  invariant(typeof uploadedFileName === 'string' && uploadedFileName.trim().length > 0,
    'createNleAssetFromUpload: uploadedFileName must be a non-empty string');
  invariant(typeof displayName === 'string' && displayName.trim().length > 0,
    'createNleAssetFromUpload: displayName must be a non-empty string');
  invariant(typeof itemId === 'string' && itemId.trim().length > 0,
    'createNleAssetFromUpload: itemId must be a non-empty string');

  const fileExtension = getFileExtension(uploadedFileName);
  const trimmedDisplayName = displayName.trim();

  return {
    name: trimmedDisplayName,
    fileName: `${trimmedDisplayName}${fileExtension}`,
    itemId: itemId.trim(),
    nleType,
    visibleTo,
    language
  };
}

/**
 * Gets the filename to use for downloading/opening a template.
 * Falls back to name with default extension if fileName is not set.
 *
 * @param asset - The NLE asset
 * @returns The filename to use for download
 * @throws Error if asset is invalid or missing required fields
 */
export function getDownloadFileName(asset: NleAsset): string {
  invariant(asset && typeof asset === 'object', 'getDownloadFileName: asset must be a valid object');

  if (asset.fileName && typeof asset.fileName === 'string' && asset.fileName.trim().length > 0) {
    return asset.fileName.trim();
  }

  invariant(asset.name && typeof asset.name === 'string' && asset.name.trim().length > 0,
    'getDownloadFileName: asset must have either fileName or name');

  const ext = getExtensionForNleType(asset.nleType);
  return `${asset.name.trim()}${ext}`;
}
